import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Building2, Loader2, MapPin, Plus, Save, Trash2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { parseMapPin } from '@/lib/mapPin'

/**
 * Admin → Website → Contact & locations.
 *
 * Writes the `storefront_contact` setting: the branches the ordering site
 * shows (in the Pickup picker and on the Contact page) plus a helpline and an
 * email for the Contact page and footer. Until it's saved the site uses the
 * receipt name, address, phone and email, so the form starts from those.
 */

interface LocationDraft {
  id: string
  name: string
  address: string
  phone: string
  hours: string
  pin: string // as typed: coordinates or a Google Maps link
  pickup: boolean
}

interface SavedLocation {
  id?: string
  name?: string
  address?: string
  phone?: string
  hours?: string
  lat?: number | null
  lng?: number | null
  pickup?: boolean | null
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

function newId(existing: LocationDraft[]): string {
  let n = existing.length + 1
  while (existing.some((l) => l.id === `loc-${n}`)) n++
  return `loc-${n}`
}

function fromSaved(l: SavedLocation, i: number): LocationDraft {
  return {
    id: str(l.id) || `loc-${i + 1}`,
    name: str(l.name),
    address: str(l.address),
    phone: str(l.phone),
    hours: str(l.hours),
    pin: typeof l.lat === 'number' && typeof l.lng === 'number' ? `${l.lat}, ${l.lng}` : '',
    pickup: l.pickup !== false,
  }
}

export function AdminStorefrontContact() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const settingsQuery = useQuery({ queryKey: ['settings', 'all'], queryFn: () => apiClient.getAllSettings() })
  const d = settingsQuery.data?.data as Record<string, unknown> | undefined

  const [locations, setLocations] = useState<LocationDraft[]>([])
  const [helpline, setHelpline] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [fromReceipt, setFromReceipt] = useState(false)

  useEffect(() => {
    if (!d) return
    const saved = d.storefront_contact as { locations?: SavedLocation[]; helpline?: string; email?: string; note?: string } | undefined
    if (saved && typeof saved === 'object') {
      setLocations((saved.locations ?? []).map(fromSaved))
      setHelpline(str(saved.helpline))
      setEmail(str(saved.email))
      setNote(str(saved.note))
      setFromReceipt(false)
      return
    }
    const name = str(d.restaurant_name) || str(d.receipt_business_name)
    const address = str(d.receipt_address)
    const phone = str(d.receipt_phone)
    setLocations(address ? [{ id: 'main', name: name || 'Main branch', address, phone, hours: '', pin: '', pickup: true }] : [])
    setHelpline(phone)
    setEmail(str(d.receipt_email))
    setNote('')
    setFromReceipt(true)
  }, [d])

  const setLoc = (i: number, patch: Partial<LocationDraft>) =>
    setLocations((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)))
  const move = (i: number, by: -1 | 1) =>
    setLocations((prev) => {
      const next = [...prev]
      const j = i + by
      if (j < 0 || j >= next.length) return prev
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })

  const badPins = locations.filter((l) => l.pin.trim() && !parseMapPin(l.pin))
  const nameless = locations.filter((l) => !l.name.trim())

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.updateSetting('storefront_contact', {
        helpline: helpline.trim(),
        email: email.trim(),
        note: note.trim(),
        locations: locations.map((l) => {
          const pin = parseMapPin(l.pin)
          return {
            id: l.id,
            name: l.name.trim(),
            address: l.address.trim(),
            phone: l.phone.trim(),
            hours: l.hours.trim(),
            pickup: l.pickup,
            ...(pin ? { lat: pin.lat, lng: pin.lng } : {}),
          }
        }),
      }),
    onSuccess: () => {
      toast({ title: 'Contact details saved', description: 'Your website shows them within a minute.' })
      setFromReceipt(false)
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
    },
    onError: (err) =>
      toast({ title: 'Could not save', description: err instanceof Error ? err.message : String(err), variant: 'destructive' }),
  })

  const canSave = nameless.length === 0 && badPins.length === 0

  return (
    <div className="space-y-5">
      <Card className="space-y-4 p-5">
        <div className="border-b border-border/60 pb-3">
          <h3 className="text-sm font-semibold">Contact page</h3>
          <p className="text-xs text-muted-foreground">
            Shown on your website's Contact us page and in its footer.
            {fromReceipt && ' Filled in from your receipt details until you save.'}
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="sc-helpline">Helpline</Label>
            <Input id="sc-helpline" value={helpline} onChange={(e) => setHelpline(e.target.value)} placeholder="0300 1234567" maxLength={40} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sc-email">Email</Label>
            <Input id="sc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="hello@yourrestaurant.pk" maxLength={120} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sc-note">Line under the page title (optional)</Label>
          <Input id="sc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="We reply to messages within an hour, 11 AM to 11 PM." maxLength={240} />
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/60 pb-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold">Branches</h3>
            <p className="text-xs text-muted-foreground">
              Customers choose one of these when they order for pickup, and see them all on the Contact page. Turn off
              pickup for an office or a delivery-only kitchen.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setLocations((prev) => [...prev, { id: newId(prev), name: '', address: '', phone: '', hours: '', pin: '', pickup: true }])}
          >
            <Plus className="h-4 w-4" /> Add branch
          </Button>
        </div>

        {locations.length === 0 && (
          <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No branches yet. Add one so pickup customers know where to collect.
          </p>
        )}

        <ol className="space-y-4">
          {locations.map((l, i) => {
            const pin = parseMapPin(l.pin)
            const pinBad = l.pin.trim() !== '' && !pin
            return (
              <li key={l.id} className="rounded-lg border p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" aria-hidden />
                  <span className="flex-1 text-sm font-semibold">{l.name.trim() || `Branch ${i + 1}`}</span>
                  <Button type="button" variant="ghost" size="icon" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" aria-label="Move down" disabled={i === locations.length - 1} onClick={() => move(i, 1)}>
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${l.name || 'branch'}`}
                    onClick={() => setLocations((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor={`sc-name-${l.id}`}>Branch name</Label>
                    <Input
                      id={`sc-name-${l.id}`}
                      value={l.name}
                      onChange={(e) => setLoc(i, { name: e.target.value })}
                      placeholder="F-10 Markaz"
                      maxLength={80}
                      aria-invalid={!l.name.trim() || undefined}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`sc-phone-${l.id}`}>Phone</Label>
                    <Input id={`sc-phone-${l.id}`} value={l.phone} onChange={(e) => setLoc(i, { phone: e.target.value })} placeholder="051 1234567" maxLength={40} />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor={`sc-address-${l.id}`}>Address</Label>
                    <Textarea
                      id={`sc-address-${l.id}`}
                      value={l.address}
                      onChange={(e) => setLoc(i, { address: e.target.value })}
                      placeholder="Plot 12, Main Double Road, F-10 Markaz, Islamabad"
                      maxLength={240}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`sc-hours-${l.id}`}>Opening times</Label>
                    <Input
                      id={`sc-hours-${l.id}`}
                      value={l.hours}
                      onChange={(e) => setLoc(i, { hours: e.target.value })}
                      placeholder="11:00 AM - 2:00 AM"
                      maxLength={60}
                    />
                    <p className="text-[11px] text-muted-foreground">Leave blank to show today's online ordering hours.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`sc-pin-${l.id}`}>Map pin</Label>
                    <Input
                      id={`sc-pin-${l.id}`}
                      value={l.pin}
                      onChange={(e) => setLoc(i, { pin: e.target.value })}
                      placeholder="Paste a Google Maps link or 33.6955, 73.0129"
                      aria-invalid={pinBad || undefined}
                    />
                    <p className={`flex items-center gap-1 text-[11px] ${pinBad ? 'text-destructive' : 'text-muted-foreground'}`}>
                      <MapPin className="h-3 w-3" />
                      {pinBad
                        ? "That link has no coordinates in it. In Google Maps, right-click your branch and copy the numbers at the top."
                        : pin
                          ? `Pinned at ${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)} — used for directions and distance.`
                          : 'Used for "Get directions" and to show customers how far away the branch is.'}
                    </p>
                  </div>
                </div>
                <label className="mt-3 flex items-center gap-2.5 text-sm">
                  <Switch checked={l.pickup} onCheckedChange={(on) => setLoc(i, { pickup: on })} aria-label={`Pickup at ${l.name || 'this branch'}`} />
                  Customers can pick up orders here
                </label>
              </li>
            )
          })}
        </ol>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border/60 pt-4">
          {!canSave && (
            <span className="text-xs text-destructive">
              {nameless.length > 0 ? 'Every branch needs a name.' : 'Fix the map pin above, or clear it.'}
            </span>
          )}
          <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !canSave} className="gap-1.5">
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save contact details
          </Button>
        </div>
      </Card>
    </div>
  )
}
